import styled from '@emotion/styled';
import { fetchInvoiceItemsOfInvoice } from '~/actions/invoice-items.actions';
import { fetchInvoice } from '~/actions/invoices.actions';
import * as blackLogo from '~/assets/logo/shippo-lockup-black-web.png';
import { ErrorMessage } from '~/components/MessageBox/Message';
import { GreenSpinner } from '~/components/Spinner';
import * as moment from 'moment';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { getDisplayInvoiceItems } from '~/selectors/invoice-items.selectors';
import { getDisplayInvoice } from '~/selectors/invoices.selectors';
import { getIsGermanShippoAddressInvoice, getUserAddress } from '~/selectors/user.selectors';
import { colors, fontWeight } from '~/styles/constants';
import Invoice from '~/types/Invoice';
import InvoiceItem from '~/types/InvoiceItem';
import { handleRequestable } from '~/utils/fetchable';
import { InjectedTranslateProps, translate } from '~/utils/translate';
import InvoiceItemsTable from './InvoiceItemsTable';
import ShippoInvoiceAddress from './ShippoInvoiceAddress';

const Container = styled.div`
  margin: 1.25rem 2.5rem 2.5rem;
  width: 700px;
  box-sizing: border-box;
  font-size: 0.8125rem;
  color: ${colors.black};
`;

const InvoiceInfoSection = styled.section`
  display: grid;
  grid-template-columns: 60% 40%;
  grid-template-rows: max-content max-content;
  grid-row-gap: 2.5rem;
`;

const InvoiceItemsSectionTitle = styled.h1`
  font-size: 1.25rem;
  margin: 1.25rem 0 0;
  padding-bottom: 0.625rem;
  font-weight: ${fontWeight.normal};
  line-height: 1.1;
  color: ${colors.black};
  border-bottom: 1px solid ${colors.containerBorderColor};
`;

const InvoiceItemsSection = styled.section`
  box-sizing: border-box;
  margin: 2.5rem 0;
  font-size: 0.875rem;
`;

interface ParamTypes {
  invoiceId: string;
}

const InvoiceDisplay = (props: InjectedTranslateProps): JSX.Element => {
  const { t } = props;
  const { invoiceId } = useParams<ParamTypes>();
  const isGermanShippoInvoice = useSelector(getIsGermanShippoAddressInvoice);
  const userAddress = useSelector(getUserAddress);
  const displayInvoice = useSelector(getDisplayInvoice);
  const displayInvoiceItems = useSelector(getDisplayInvoiceItems);
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(fetchInvoice(invoiceId));
    dispatch(fetchInvoiceItemsOfInvoice(invoiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderShippoAddress = React.useCallback(
    () => (
      <section className="text-right">
        <br />
        <ShippoInvoiceAddress isGerman={isGermanShippoInvoice} />
      </section>
    ),
    [isGermanShippoInvoice],
  );

  const renderUserAddress = React.useCallback(
    () =>
      userAddress ? (
        <section>
          <strong>{userAddress.name}</strong>
          <br />
          {userAddress.company}
          <br />
          {userAddress.street1} {userAddress.streetNumber}
          <br />
          {userAddress.street2 && (
            <>
              {userAddress.street2}
              <br />
            </>
          )}
          {userAddress.city && `${userAddress.city}, `}
          {userAddress.state && `${userAddress.state}, `}
          {userAddress.zip}
          <br />
          {userAddress.country?.iso2}
        </section>
      ) : null,
    [userAddress],
  );

  const renderErrorMessage = (errorMessage: string) => (
    <ErrorMessage withIcon>{errorMessage}</ErrorMessage>
  );

  const renderInvoiceInfo = React.useCallback(() => {
    return handleRequestable<Invoice, string>(
      () => {},
      () => (
        <div className="justify-self-end">
          <GreenSpinner />
        </div>
      ),
      (invoice) => (
        <section className="text-right">
          <strong>
            {t('account.billing.invoices.download.invoiceNumber', {
              invoiceNumber: invoice.invoiceNumber,
            })}
          </strong>
          <br />
          {t('account.billing.invoices.download.invoiceDate', {
            invoiceDate: moment.utc(invoice.objectCreated).format('LL'),
          })}
          <br />
        </section>
      ),
      () => {},
    )(displayInvoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayInvoice]);

  const renderInvoiceItems = React.useCallback(() => {
    return handleRequestable<InvoiceItem[], string>(
      () => {},
      () => (
        <div className="flex justify-center mt-4">
          <GreenSpinner />
        </div>
      ),
      (invoiceItems) =>
        displayInvoice.kind === 'success' ? (
          <InvoiceItemsTable invoiceItems={invoiceItems} invoice={displayInvoice.data} />
        ) : (
          renderErrorMessage(t('account.billing.invoices.download.errors.retrieveInvoice'))
        ),
      () => renderErrorMessage(t('account.billing.invoices.download.errors.retrieveInvoiceItems')),
    )(displayInvoiceItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayInvoiceItems, displayInvoice]);

  return (
    <Container>
      <Helmet>
        <title>{t('account.billing.invoices.header')} - Shippo</title>
      </Helmet>
      {displayInvoice.kind === 'failed' ? (
        renderErrorMessage(t('account.billing.invoices.download.errors.retrieveInvoice'))
      ) : (
        <React.Fragment>
          <InvoiceInfoSection>
            <img className="self-center" src={blackLogo} width="160" alt="Shippo logo" />
            {renderShippoAddress()}
            {renderUserAddress()}
            {renderInvoiceInfo()}
          </InvoiceInfoSection>
          <InvoiceItemsSection>
            <InvoiceItemsSectionTitle>
              {t('account.billing.invoices.download.invoiceItemsTable.title')}
            </InvoiceItemsSectionTitle>
            {renderInvoiceItems()}
          </InvoiceItemsSection>
        </React.Fragment>
      )}
    </Container>
  );
};

const ComposedInvoiceDisplay = translate('settings')(InvoiceDisplay);

export default ComposedInvoiceDisplay;
